/* eslint-disable react/prop-types */
import React from 'react';
import { CA_REPORT, ReportStatusEnum } from './report.type';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Stack, Tooltip, Menu, Badge } from '@mui/material';
import {
  Add,
  Description,
  ExpandMore,
  Launch,
  Delete as DeleteIcon,
  Place,
  ViewColumnOutlined as ViewColumnIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { Button, Typography } from 'components/index';
import { REPORT_COLORS } from './constants';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../common/constants/urls';
import { capitalize, format, formatCurrency } from '../../common/utils/common.util';
import { TOOLTIPS } from './report.util';
import WhiteFab from '../common/WhiteFab';
import { CustomTooltip } from '../common/CustomTooltip';
import ComparablesMap from '../map/ComparablesMap';
import { UnassignedBudgetCell } from '../project/projects-list/UnassignedBudgetCell';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
  type MRT_VisibilityState,
  useMaterialReactTable,
} from 'material-react-table';
import { CustomManageColumnsMenu } from '../common/CustomManageColumnsMenu';
import { useMRTStyling } from '../../hooks/useMRTStyling';
import { IconOverTextButton } from '../common/IconOverTextButton';
import { ButtonDivider } from '../common/ButtonDivider';
import { GridFilterModel, GridLogicOperator, GridColDef } from '@mui/x-data-grid';
import { getStringOperators, getNumericOperators } from '../../common/utils/datagrid-sieve.util';
import FilterPanel from '../filter-panel/FilterPanel';
import { getAppliedFiltersCount } from '../filter-panel/filter-panel.util';
interface Props {
  report: CA_REPORT;
  compLoading: boolean;
  isLocked: boolean;
  isDeletingComp: boolean;
  deletingCompId: string | null;
  onDeleteComp: (compId: string) => void;
  handleOpenCompsDialog: () => void;
  selectedComparableIds: string[];
  setSelectedComparableIds: React.Dispatch<React.SetStateAction<string[]>>;
  onSelectDivisionsForComps: () => void;
}

const CAComparableDetails: React.FC<Props> = ({
  report,
  compLoading,
  isLocked,
  isDeletingComp,
  deletingCompId,
  onDeleteComp,
  handleOpenCompsDialog,
  selectedComparableIds,
  setSelectedComparableIds,
  onSelectDivisionsForComps,
}) => {
  const tableRef = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [showMap, setShowMap] = React.useState(false);
  const mapsEnabled = process.env.REACT_APP_GOOGLE_MAPS_ENABLED == 'true';
  const [mapHeight, setMapHeight] = React.useState('400px');
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = React.useState<MRT_VisibilityState>({
    siteAcreage: false,
    storyCount: false,
    buildingCount: false,
  });
  const [sorting, setSorting] = React.useState<any>([]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({ items: [], logicOperator: GridLogicOperator.And });
  const { getOnSortingChangeWithToast } = useMRTStyling<any>();

  const handleShowMap = (e: any) => {
    e.stopPropagation();
    setShowMap(prevState => !prevState);
  };

  const handleChange = (isExpanded: boolean) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const handleAddCompsClick = (e: any) => {
    e.stopPropagation();
    handleOpenCompsDialog();
  };

  const showManageColumns = (e: any) => {
    e.stopPropagation();
    setColumnsMenuAnchorEl(e.currentTarget);
  };

  const openFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilter = () => {
    setFilterAnchorEl(null);
  };

  // Define filterable columns for comparables
  const filterColumns: GridColDef[] = [
    {
      field: 'project.projectTitle',
      headerName: 'Project',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'budget',
      headerName: 'Total Budget',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'grossBuildingSF',
      headerName: 'Gross Building Area (SF)',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'netRentableSF',
      headerName: 'Net Rentable Area (SF)',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'unitCount',
      headerName: 'Unit Count',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'siteAcreage',
      headerName: 'Site Acreage',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'buildingCount',
      headerName: 'Building Count',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'storyCount',
      headerName: 'Story Count',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'project.projectAddress.city.name',
      headerName: 'City',
      type: 'string',
      filterOperators: getStringOperators(),
    },
  ];

  const skippedFields: string[] = [];

  React.useEffect(() => {
    // When the map is shown, set the map height to the table height dynamically
    if (showMap && tableRef.current) {
      const tableHeight = tableRef.current.offsetHeight;
      setMapHeight(`${tableHeight}px`);
    }
  }, [showMap]);
  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: 'project',
        header: 'Project',
        Cell: ({ row }) => {
          const comp = row.original;
          if (!comp) return '--';
          return (
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              p={0}
              height={'100%'}
              sx={{
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'unset',
                cursor: 'pointer',
              }}
            >
              <Link to={`${ROUTES.COMPS}/${comp.id}`} target='_blank' style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <CustomTooltip title={comp.project.projectTitle}>
                  <Typography
                    display='inline-block'
                    fontWeight='light'
                    fontSize='medium'
                    noWrap
                    sx={{
                      flexGrow: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {comp.project.projectTitle}
                  </Typography>
                </CustomTooltip>
                <Launch sx={{ fontSize: '16px', ml: '5px' }} color='primary' />
              </Link>
            </Stack>
          );
        },
        size: 220,
      },
      {
        id: 'unassignedBudget',
        header: 'Unallocated Budget',
        size: 120,
        Cell: ({ row }) => <UnassignedBudgetCell totalBudget={row.original.budget} unassignedBudget={row.original.unassignedBudget} />,
      },
      {
        accessorKey: 'budget',
        header: 'Total Budget',
        size: 140,
        Cell: ({ cell }) => (cell.getValue<number>() ? formatCurrency(String(cell.getValue<number>())) : '--'),
      },
      {
        accessorKey: 'grossBuildingSF',
        header: 'Gross Building Area (SF)',
        size: 140,
        Cell: ({ cell }) => (cell.getValue<number>() ? format(String(cell.getValue<number>()), 'number') : '--'),
      },
      {
        accessorKey: 'netRentableSF',
        header: 'Net Rentable Area (SF)',
        size: 140,
        Cell: ({ cell }) => (cell.getValue<number>() ? format(String(cell.getValue<number>()), 'number') : '--'),
      },
      {
        accessorKey: 'unitCount',
        header: 'Unit Count',
        size: 100,
        Cell: ({ cell }) => (cell.getValue<number>() ? format(String(cell.getValue<number>()), 'number') : '--'),
      },
      {
        accessorKey: 'siteAcreage',
        header: 'Site Acreage',
        size: 120,
        Cell: ({ cell }) => (cell.getValue<number>() ? format(String(cell.getValue<number>()), 'number') : '--'),
      },
      {
        accessorKey: 'buildingCount',
        header: 'Building Count',
        size: 120,
        Cell: ({ cell }) => (cell.getValue<number>() ? format(String(cell.getValue<number>()), 'number') : '--'),
      },
      {
        accessorKey: 'storyCount',
        header: 'Story Count',
        size: 100,
        Cell: ({ cell }) => (cell.getValue<number>() ? format(String(cell.getValue<number>()), 'number') : '--'),
      },
      {
        id: 'city',
        header: 'City',
        size: 120,
        Cell: ({ row }) => capitalize(row.original?.project?.projectAddress?.city?.name || '--'),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        enableHiding: true,
        size: 80,
        Cell: ({ row }) => {
          const comp = row.original;
          return (
            <Tooltip
              title={
                (isDeletingComp && deletingCompId !== comp.id) || (isLocked && report.status === ReportStatusEnum.Completed)
                  ? TOOLTIPS.statusCompleted
                  : isLocked
                    ? TOOLTIPS.unlockToEdit
                    : ''
              }
              disableHoverListener={!isLocked}
              arrow={false}
            >
              <Box margin={'auto'}>
                <WhiteFab
                  size='small'
                  aria-label='delete'
                  icon={<DeleteIcon fontSize='small' />}
                  loading={isDeletingComp && deletingCompId === comp.id}
                  textColor='error'
                  disabled={(isDeletingComp && deletingCompId !== comp.id) || isLocked}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onDeleteComp(comp.id);
                  }}
                />
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [isDeletingComp, deletingCompId, isLocked, report.status, onDeleteComp],
  );

  // Apply filters to comparables
  const filteredComps = React.useMemo(() => {
    if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
      return report.selectedComps;
    }

    return report.selectedComps.filter((comp: any) => {
      return filterModel.items.every(filterItem => {
        const { field, operator, value } = filterItem;
        if (!value && value !== 0) return true;

        // Get the nested property value
        const getValue = (obj: any, path: string) => {
          return path.split('.').reduce((current, prop) => current?.[prop], obj);
        };

        const fieldValue = getValue(comp, field);
        if (fieldValue === undefined || fieldValue === null) return false;

        // Convert to string for string comparisons
        const fieldValueStr = String(fieldValue).toLowerCase();
        const valueStr = String(value).toLowerCase();

        switch (operator) {
          case 'contains':
            return fieldValueStr.includes(valueStr);
          case 'equals':
            return fieldValue === value || fieldValueStr === valueStr;
          case 'startsWith':
            return fieldValueStr.startsWith(valueStr);
          case 'endsWith':
            return fieldValueStr.endsWith(valueStr);
          case 'isEmpty':
            return !fieldValue || fieldValueStr === '';
          case 'isNotEmpty':
            return fieldValue && fieldValueStr !== '';
          case '>':
            return Number(fieldValue) > Number(value);
          case '>=':
            return Number(fieldValue) >= Number(value);
          case '<':
            return Number(fieldValue) < Number(value);
          case '<=':
            return Number(fieldValue) <= Number(value);
          case '=':
            return Number(fieldValue) === Number(value);
          case '!=':
            return Number(fieldValue) !== Number(value);
          default:
            return true;
        }
      });
    });
  }, [report.selectedComps, filterModel]);

  // Sync MRT rowSelection from parent ids
  React.useEffect(() => {
    if (isLocked) {
      setRowSelection({});
      return;
    }
    const mapped = selectedComparableIds.reduce((acc: MRT_RowSelectionState, id) => {
      acc[id] = true;
      return acc;
    }, {} as MRT_RowSelectionState);
    setRowSelection(mapped);
  }, [selectedComparableIds, isLocked]);

  const table = useMaterialReactTable({
    columns,
    data: filteredComps,
    getRowId: (row: any) => row.id,
    enableRowSelection: !isLocked,
    enableMultiRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableColumnFilters: false,
    enableColumnActions: true,
    layoutMode: 'grid',
    state: {
      isLoading: compLoading,
      rowSelection,
      density: 'compact',
      columnVisibility,
      sorting,
    },
    onRowSelectionChange: updater => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(next);
      if (!isLocked) {
        const ids = Object.keys(next).filter(k => next[k]);
        setSelectedComparableIds(ids);
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: getOnSortingChangeWithToast(sorting as any, setSorting as any),
    initialState: {
      columnPinning: {
        left: ['mrt-row-select'],
      },
    },
    muiTableContainerProps: {
      sx: {
        flex: '1 1 auto',
        backgroundColor: 'background.paper',
        maxWidth: '100%',
        overflowX: 'auto',
      },
      ref: tableRef,
    },
    muiTablePaperProps: {
      sx: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
      },
    },
    muiTableHeadProps: {
      sx: {
        padding: 0,
        color: 'inherit',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: REPORT_COLORS.COMPS_TABLE_COLOR,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: REPORT_COLORS.COMPS_TABLE_COLOR + ' !important',
        color: theme => theme.palette.text.primary,
        fontFamily: theme => theme.typography.fontFamily,
        fontSize: theme => (typeof theme.typography.body2.fontSize === 'string' ? theme.typography.body2.fontSize : '0.875rem'),
        fontWeight: theme => theme.typography.fontWeightMedium,
        justifyContent: 'center',
        padding: '12px 9px',
        minHeight: '48px',
        border: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        // Add these new properties for text wrapping
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        textAlign: 'center',
        '& .MuiBox-root': {
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const backgroundColor = row.index % 2 === 0 ? '#ffffff' : '#f9f9f9';
      return {
        sx: {
          borderBottom: 'none',
          backgroundColor: backgroundColor + ' !important',
        },
      };
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        backgroundColor: 'transparent !important',
      },
    },
  });

  return (
    <Accordion elevation={2} sx={{ borderRadius: '10px !important' }} expanded={expanded} onChange={handleChange(!expanded)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          '& .MuiAccordionSummary-content': { justifyContent: 'space-between' },
        }}
      >
        <Box display='flex' alignItems='center'>
          <Description color='primary' />
          <Typography variant='h6' color='primary' sx={{ ml: 1, mb: 0 }}>
            Comparables ({report.selectedComps.length})
          </Typography>
        </Box>
        {expanded && (
          <Box display='flex' justifyContent='flex-end' alignItems='center' sx={{ ml: 'auto' }}>
            {selectedComparableIds.length > 0 ? (
              <Button variant='contained' color='primary' onClick={onSelectDivisionsForComps} sx={{ mx: 1 }}>
                Edit Divisions
              </Button>
            ) : (
              <Stack direction='row' alignItems='center' spacing={0} sx={{ ml: 'auto', mr: 2 }}>
                <IconOverTextButton
                  icon={<Place />}
                  text={showMap ? 'Hide Map' : 'Show Map'}
                  tooltip={showMap ? 'Click to hide map view' : 'Click to show map view'}
                  onClick={handleShowMap}
                  showHoverEffect={true}
                />

                <Tooltip
                  title={
                    isLocked && report.status === ReportStatusEnum.Completed
                      ? TOOLTIPS.statusCompleted
                      : isLocked
                        ? TOOLTIPS.unlockToEdit
                        : ''
                  }
                  disableHoverListener={!isLocked}
                  arrow={false}
                >
                  <span>
                    <IconOverTextButton
                      icon={<Add />}
                      text='Add Comps'
                      tooltip='Add comparable projects'
                      onClick={handleAddCompsClick}
                      disabled={isLocked}
                      showHoverEffect={true}
                    />
                  </span>
                </Tooltip>

                <ButtonDivider />

                <IconOverTextButton
                  icon={<ViewColumnIcon />}
                  text='Columns'
                  tooltip='Manage Columns'
                  onClick={showManageColumns}
                  showHoverEffect={false}
                  wrapInSpan={true}
                />

                <Badge badgeContent={getAppliedFiltersCount(filterModel, skippedFields)} color='primary' overlap='circular'>
                  <IconOverTextButton
                    icon={<FilterListIcon />}
                    text='Filter'
                    tooltip='Filter comparables'
                    onClick={openFilter}
                    showHoverEffect={false}
                  />
                </Badge>
              </Stack>
            )}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', height: '100%' }}>
        <Grid container spacing={2} sx={{ flexGrow: 1, height: '100%' }}>
          {/* Left column for the table */}
          <Grid size={{ xs: showMap ? 6 : 12 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Stack sx={{ overflowX: 'auto' }}>
                <MaterialReactTable table={table} />
              </Stack>
            </Box>
          </Grid>

          {/* Right column for the map, only shown when showMap is true */}
          {showMap && (
            <Grid size={{ xs: 6 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flexGrow: 1, height: mapHeight }}>
                {mapsEnabled ? (
                  <ComparablesMap selectedComps={report.selectedComps} isFromCAReport={true} />
                ) : (
                  <p style={{ marginLeft: '20px', color: '#555', fontSize: '16px', fontStyle: 'italic' }}>Maps are disabled.</p>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
      {/* Filter Panel Menu */}
      <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={closeFilter} onClick={e => e.stopPropagation()}>
        <FilterPanel columns={filterColumns} filterModel={filterModel} setFilterModel={setFilterModel} skippedFields={skippedFields} />
      </Menu>
      {/* Manage Columns Menu */}
      <CustomManageColumnsMenu anchorEl={columnsMenuAnchorEl} setAnchorEl={setColumnsMenuAnchorEl} table={table} title={'Manage Columns'} />
    </Accordion>
  );
};

const gridStyles = {};

export default CAComparableDetails;
